// Admin API fetch wrapper.
//
// Auth strategy (post-XSS-hardening):
//   • admin-api (/admin/*, /auth/*)  — admin_session httpOnly cookie set by
//     admin-api on login. Browsers send it automatically when credentials:'include'
//     is set. JavaScript here cannot read or write it — XSS-safe.
//   • main HCM API (/hcm/*, /api/super-admin/*) — bearer access token in
//     localStorage. The main API still uses Authorization: Bearer for parity
//     with the worker / mobile flows; the refresh token lives in an httpOnly
//     cookie there too.
//
// Two bases:
//   API_BASE       — main HCM API. Set VITE_API_BASE for prod (e.g.
//                    https://api.hcmcrm.com). Falls back to "" (same origin)
//                    in dev so the Vite proxy forwards to localhost:3000.
//   ADMIN_API_BASE — isolated admin-api service (all /admin/* routes). Set
//                    VITE_ADMIN_API_BASE for prod (e.g. https://admin-api.hcmcrm.com).
const API_BASE: string       = import.meta.env["VITE_API_BASE"] ?? "";
const ADMIN_API_BASE: string = import.meta.env["VITE_ADMIN_API_BASE"] ?? "";

// Access token lives in sessionStorage (survives F5 refresh, cleared on tab close — XSS-safer than localStorage).
const SESSION_TOKEN_KEY = "hcm_access_token";
let _accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  _accessToken = token;
  if (token) sessionStorage.setItem(SESSION_TOKEN_KEY, token);
  else sessionStorage.removeItem(SESSION_TOKEN_KEY);
}

export function getAccessToken(): string | null {
  if (_accessToken) return _accessToken;
  const fromSession = sessionStorage.getItem(SESSION_TOKEN_KEY);
  if (fromSession) { _accessToken = fromSession; return _accessToken; }
  // Migration: pre-sessionStorage builds stored the token in localStorage as
  // "hcm_token". Lift it into the session store on first access so existing
  // sessions don't 401 after the upgrade. The localStorage entry is then
  // cleared so we don't read a stale token after sign-out.
  const legacy = localStorage.getItem("hcm_token");
  if (legacy) {
    localStorage.removeItem("hcm_token");
    setAccessToken(legacy);
  }
  return _accessToken;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function baseFetch<T>(base: string, path: string, init: RequestInit): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  // Main HCM API still uses bearer auth — read access token from sessionStorage.
  // Admin-api auth comes entirely from the admin_session httpOnly cookie:
  // we cannot read it here, but `credentials: 'include'` makes the browser
  // attach it automatically.
  const isMainApi = path.startsWith("/hcm") || path.startsWith("/api/super-admin");
  if (isMainApi) {
    const token = getAccessToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  const res = await fetch(`${base}${path}`, { ...init, headers, credentials: "include" });
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { body = null; }
    throw new ApiError(res.status, body, `API ${res.status}: ${path}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** Main HCM API — use for auth endpoints. */
export function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  return baseFetch<T>(API_BASE, path, init);
}

/** Isolated admin-api service — use for all /admin/* operations. */
export function adminFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  return baseFetch<T>(ADMIN_API_BASE, path, init);
}
